import React from "react";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import GenericMultiDropdown from "@ui-components/Dropdown/GenericMultiDropdown";
import GenericTooltip from "@ui-components/Tooltip/GenericTooltip";
import { getServiceDropdownLabel } from "../../utils/tickets";
import type { CustomerInfo, ServiceItem } from "../../utils/tickets";

interface KontrolEdgeServiceSelectionProps {
	/** Partner view also has to say which end customer the ticket is for. */
	isPartnerView: boolean;
	uniqueCustomers: CustomerInfo[];
	/** Already narrowed to the selected customer by the calling modal. */
	filteredServiceItems: ServiceItem[];
	selectedCustomerId: number | null;
	selectedCustomerNameDisplay: string;
	selectedServiceLabels: string[];
	onCustomerSelect: (customerNameDisplay: string) => void;
	onServiceSelect: (selectedLabels: string[]) => void;
	servicesTooltip?: string;
}

/**
 * Customer + service(s) pickers shared by the Kontrol Edge ticket modals.
 *
 * The pair travels together: the services list is derived from the customer,
 * so splitting them across surfaces is what let Help Request ship without any
 * service at all (CTX-533). Both feed `subscriptionID`, which the backend
 * sends to KS as the Service ID custom field.
 */
export const KontrolEdgeServiceSelection: React.FC<
	KontrolEdgeServiceSelectionProps
> = ({
	isPartnerView,
	uniqueCustomers,
	filteredServiceItems,
	selectedCustomerId,
	selectedCustomerNameDisplay,
	selectedServiceLabels,
	onCustomerSelect,
	onServiceSelect,
	servicesTooltip = "Select one or more services.",
}) => {
	// Without a customer there is nothing to list, so the services dropdown
	// stays closed until one is picked rather than showing an empty menu.
	const servicesDisabled = isPartnerView && !selectedCustomerId;

	return (
		<div className="flex flex-col sm:flex-row gap-4">
			{isPartnerView && (
				<div className="w-full sm:w-1/2">
					<label className="block mb-1 font-medium text-semantic-text-secondary">
						Select Customer <span className="text-red-500">*</span>
					</label>
					<GenericDropdown
						items={uniqueCustomers.map((c) => c.nameDisplay)}
						value={selectedCustomerNameDisplay}
						onSelect={onCustomerSelect}
						defaultLabel="Select a customer"
						minWidthClass=""
						maxWidthClass=""
						widthClass="w-full"
						dropdownMaxHeightClass="max-h-48"
						fixedPosition={true}
					/>
				</div>
			)}
			<div className={isPartnerView ? "w-full sm:w-1/2" : "w-full"}>
				<label className="block mb-1 font-medium text-semantic-text-secondary flex items-center gap-1">
					Select Service(s) <span className="text-red-500">*</span>
					<GenericTooltip text={servicesTooltip} position="top" />
				</label>
				<GenericMultiDropdown
					items={filteredServiceItems.map(getServiceDropdownLabel)}
					value={selectedServiceLabels}
					onSelect={onServiceSelect}
					defaultLabel="Select service(s)"
					minWidthClass=""
					maxWidthClass=""
					widthClass="w-full"
					disabled={servicesDisabled}
					fixedPosition={true}
					dropdownMaxHeightClass="max-h-48"
				/>
			</div>
		</div>
	);
};

export default KontrolEdgeServiceSelection;
